import type {
  ActionRejected,
  ChatMsg,
  EmoteBroadcast,
  LeaderboardMessage,
  PlayerEvent,
  PositionCorrection,
  ProjectPhase,
  ProjectStartedMessage,
  RadioSnapshot,
  Results,
  WelcomeMessage,
} from "@agency/shared";
import { RADIO_OFF } from "@agency/shared";

/** Structural view of a synchronized player, good enough for the UI and scene. */
export interface PlayerSnapshot {
  sessionId: string;
  nickname: string;
  avatarId: string;
  role: string;
  x: number;
  y: number;
  dir: number;
  moving: boolean;
  activity: string;
  deskId: string;
  exhaustion: number;
  burnedOut: boolean;
  workSeconds: number;
  connected: boolean;
}

export type ConnectionState = "idle" | "connecting" | "joined" | "reconnecting" | "closed";

export interface ProjectSnapshot {
  id: number;
  name: string;
  requiredUnits: number;
  completedUnits: number;
}

export interface StoreEvents {
  welcome: WelcomeMessage;
  playerAdd: { sessionId: string; player: PlayerSnapshot };
  playerRemove: { sessionId: string };
  playerEvent: PlayerEvent;
  deskChange: { deskId: string; occupantId: string };
  phase: ProjectPhase;
  project: ProjectSnapshot;
  chat: ChatMsg;
  emote: EmoteBroadcast;
  results: Results;
  projectStarted: ProjectStartedMessage;
  leaderboard: LeaderboardMessage;
  rejected: ActionRejected;
  correction: PositionCorrection;
  connection: { state: ConnectionState; code?: number; message?: string };
  toast: { text: string; tone?: "info" | "error" | "success" };
  exitZone: { inside: boolean };
  /** The server's desired radio state changed. */
  radio: RadioSnapshot;
  radioArea: { inside: boolean };
  /** Proximity gain (0–1) for the radio, smoothed by the scene. */
  radioGain: number;
}

/** A tiny typed event emitter plus a snapshot of the latest local state. */
export class GameStore {
  private handlers = new Map<keyof StoreEvents, Set<(payload: unknown) => void>>();

  sessionId = "";
  nickname = "";
  /** serverTime − Date.now(), from the welcome message. */
  serverOffset = 0;
  timeScale = 1;
  lastWelcome?: WelcomeMessage;
  phase: ProjectPhase = "active";
  project: ProjectSnapshot = { id: 0, name: "", requiredUnits: 0, completedUnits: 0 };
  readonly players = new Map<string, PlayerSnapshot>();
  /** deskId → occupant sessionId ("" when free). */
  readonly desks = new Map<string, string>();
  radio: RadioSnapshot = { ...RADIO_OFF };
  radioGain = 1;

  on<K extends keyof StoreEvents>(event: K, cb: (payload: StoreEvents[K]) => void): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    const handler = cb as (payload: unknown) => void;
    set.add(handler);
    return () => set?.delete(handler);
  }

  emit<K extends keyof StoreEvents>(event: K, payload: StoreEvents[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of [...set]) handler(payload as unknown);
  }

  countOnline(): number {
    let n = 0;
    for (const p of this.players.values()) if (p.connected) n++;
    return n;
  }

  countWorking(): number {
    let n = 0;
    for (const p of this.players.values()) if (p.activity === "working") n++;
    return n;
  }
}

export const store = new GameStore();
